import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme
} from '@mui/material';
import { FaSearch, FaTrash } from 'react-icons/fa';
import { Pencil } from 'lucide-react';
import { useColorMode } from '../../../../../theme/ThemeContext';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import Table from '../../../admincomponents/Table';

const API_URL = 'https://tdtlworld.com/dss-kaas-backend/master/subdivisions/';
const DIVISION_API_URL = 'https://tdtlworld.com/dss-kaas-backend/master/divisions/';

const SubdivisionManager = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const [subdivisions, setSubdivisions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [subdivisionInput, setSubdivisionInput] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);



  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    fetchSubdivisions();
    fetchDivisions();
  }, []);

  const fetchSubdivisions = async () => {
    setIsPageLoading(true);
    try {
      const res = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire('Error', error.detail || 'Failed to fetch subdivisions', 'error');
        return;
      }

      const data = await res.json();
      setSubdivisions(data);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Network error', 'error');
    } finally {
      setIsPageLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const res = await fetch(DIVISION_API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        console.error('Failed to fetch divisions:', error);
        return;
      }

      const data = await res.json();
      setDivisions(data);
    } catch (err) {
      console.error('Error fetching divisions:', err);
    }
  };

  const filteredSubdivisions = subdivisions.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.division && s.division.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );



  const handleShowModal = (subdivision = null) => {
    if (subdivision) {
      setSubdivisionInput(subdivision.name);
      setSelectedDivision(subdivision.division?.id || '');
      setEditItem(subdivision);
    } else {
      setSubdivisionInput('');
      setSelectedDivision('');
      setEditItem(null);
    }
    setShowModal(true);
  };

  const handleClose = () => {
    setSubdivisionInput('');
    setSelectedDivision('');
    setEditItem(null);
    setShowModal(false);
  };

  const columns = [
    { 
      key: 'srNo', 
      label: 'Sr No.', 
      render: (row, index) => (
        <Typography variant="body2" color="text.secondary">
          {index + 1}
        </Typography>
      )
    },
    { 
      key: 'name', 
      label: 'Subdivision', 
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.primary" fontWeight={500}>
          {row.name}
        </Typography>
      )
    },
    { 
      key: 'division', 
      label: 'Division', 
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.division?.name || 'N/A'}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleShowModal(row)}
            sx={{ color: 'primary.main' }}
          >
            <Pencil size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            disabled={isDeletingId === row.id}
            sx={{ color: '#ff5370' }}
          >
            {isDeletingId === row.id ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FaTrash size={16} />
            )}
          </IconButton>
        </Box>
      )
    }
  ];

  const handleAddOrUpdate = async () => {
    const trimmed = subdivisionInput.trim();
    if (!trimmed || !selectedDivision) {
      Swal.fire('Validation', 'Both subdivision name and division are required!', 'warning');
      return;
    }

    setIsLoading(true);
    const method = editItem ? 'PATCH' : 'POST';
    const url = editItem ? `${API_URL}${editItem.id}/` : API_URL;
    const payload = {
      name: trimmed,
      division: selectedDivision
    };

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire({
          title: 'Error',
          text: error.detail || `Failed to ${editItem ? 'update' : 'add'} subdivision`,
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
        return;
      }

      Swal.fire({
        title: editItem ? 'Updated!' : 'Added!',
        text: `Subdivision ${editItem ? 'updated' : 'added'} successfully.`,
        icon: 'success',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      });
      fetchSubdivisions();
      handleClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: 'Error',
        text: 'Network error',
        icon: 'error',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (subdivision) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${subdivision.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: theme.palette.text.secondary,
      confirmButtonText: 'Yes, delete it!',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    });

    if (confirm.isConfirmed) {
      setIsDeletingId(subdivision.id);

      try {
        const res = await fetch(`${API_URL}${subdivision.id}/`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const error = await res.json();
          Swal.fire({
            title: 'Error',
            text: error.detail || 'Failed to delete',
            icon: 'error',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          });
          return;
        }

        Swal.fire({
          title: 'Deleted!',
          text: 'Subdivision deleted successfully.',
          icon: 'success',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          confirmButtonColor: theme.palette.primary.main,
        });
        fetchSubdivisions();
      } catch (err) {
        console.error(err);
        Swal.fire({
          title: 'Error',
          text: 'Network error',
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
      } finally {
        setIsDeletingId(null);
      }
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
    border: `1px solid ${theme.palette.divider}`
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, color: 'text.primary', textAlign: 'center' }}>
        Manage Subdivisions
      </Typography>

      <Card sx={{ 
        p: 3, 
        mb: 4, 
        bgcolor: 'background.paper',
        color: 'text.primary'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search Subdivisions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleShowModal()}
          >
            + Add Subdivision
          </Button>
        </Box>

        {isPageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Table
            columns={columns}
            data={filteredSubdivisions}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Card>

      <Modal
        open={showModal}
        onClose={handleClose}
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
            {editItem ? 'Edit' : 'Add'} Subdivision
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Division</InputLabel>
            <Select
              value={selectedDivision}
              label="Division"
              onChange={(e) => setSelectedDivision(e.target.value)}
              required
            >
              <MenuItem value=""><em>Select Division</em></MenuItem>
              {divisions.map((division) => (
                <MenuItem key={division.id} value={division.id}>
                  {division.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter subdivision name"
            value={subdivisionInput}
            onChange={(e) => setSubdivisionInput(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleClose} 
              disabled={isLoading}
              sx={{ color: 'text.primary', borderColor: 'divider' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddOrUpdate}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {editItem ? 'Update' : 'Add'} Subdivision
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SubdivisionManager;
